import { useEffect, useState } from "react";
import ability from "../../../../../casl/Can";
import {
  AppBox,
  AppPaper,
  AppGridBox,
  AppModal,
  AppSkeletonGlobalCardList,
  AppGroup,
} from "../../../../../commons/components";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import { SeasonStatusEnum } from "../../../../../commons/enums/season-status-enum";
import { ManageFootballCompetition } from "../../../../../commons/models/football/admin/manage-football-competition";
import { ManageFootballTeam } from "../../../../../commons/models/football/admin/manage-football-team";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { EmptyTeamInviteComponent } from "../tournamentEvents/EmptyTeamInviteComponent";
import IconAddCircle from "../../../../../commons/components/icons/add-circle";
import TeamSearch from "../TeamSearch";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import TournamentSeasonInvitedTeamCard from "./TournamentSeasonInvitedTeamCard";
import { fetchCompetitionSeasonInvitedTeams } from "../../../../../redux/slices/football/admin/competition/manage-competition-season-invited-teams-slice";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import {
  competitionSeasonTeamsInvite,
  IManageCompetitionTeamsInviteOption,
} from "../../../../../redux/slices/football/admin/competition/manage-competition-season-teams-invite-slice";
import { IFootballHostedCompetition } from "../../../../../commons/models/football/interface/i-football-competition";

export const TeamInvitation = () => {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { fetchedCompetition } = useAppSelector((state) => state.fetchedCompetition);
  const { selectedGlobalSeasonOption } = useAppSelector((state) => state.globalSeasonSelect);
  const { competitionSeasonInvitedTeams, competitionSeasonInvitedTeamsStatus } = useAppSelector(
    (state) => state.footballManageCompetitionSeasonInvitedTeams
  );
  const [isTeamInvite, setTeamInvite] = useState<boolean>(false);
  const [isGroupShow, setIsGroupShow] = useState(true);
  useEffect(() => {
    if (fetchedCompetition.id && fetchedCompetition?.latestSeason?.id) {
      dispatch(
        fetchCompetitionSeasonInvitedTeams({
          competitionId: fetchedCompetition.id,
          seasonId: fetchedCompetition.latestSeason.id,
          urlParams: {
            hostTeamId: (fetchedCompetition as IFootballHostedCompetition).hostTeam?.id,
          },
        })
      );
    }
  }, [fetchedCompetition]);

  const handleSelectedTeams = (teams: ManageFootballTeam[]) => {
    if (teams.length > 0) {
      const selectedTeamIds: number[] = [];
      teams.forEach((team) => {
        selectedTeamIds.push(team.id);
      });

      const seasonTeamsOptions: IManageCompetitionTeamsInviteOption = {
        competitionId: fetchedCompetition.id,
        seasonId: Number(selectedGlobalSeasonOption.value),
        teamsInfo: { teamIds: selectedTeamIds },
        urlParams: {
          hostTeamId: (fetchedCompetition as IFootballHostedCompetition).hostTeam?.id,
        },
      };
      dispatch(competitionSeasonTeamsInvite(seasonTeamsOptions))
        .unwrap()
        .then(() => {
          addSnackbar({
            key: "team-invite",
            text: "Team(s) Invited Successfully",
            variant: "success",
          });
          dispatch(
            fetchCompetitionSeasonInvitedTeams({
              competitionId: fetchedCompetition.id,
              seasonId: fetchedCompetition.latestSeason?.id as number,
              urlParams: {
                hostTeamId: (fetchedCompetition as IFootballHostedCompetition).hostTeam?.id,
              },
            })
          );
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
      setTeamInvite(false);
    }
  };

  const handleToggleShowHide = (value: boolean) => {
    if (selectedGlobalSeasonOption.secondaryValue?.status === SeasonStatusEnum.Active) {
      setIsGroupShow(value);
    }
  };

  return (
    <>
      {competitionSeasonInvitedTeamsStatus === StatusEnum.Loading ? (
        <AppSkeletonGlobalCardList numberOfItems={2} withAvatar withText />
      ) : (
        <AppGroup
          title="Team Invitation"
          withButton={
            (ability.can(
              RolePolicyActionEnum.UPDATE,
              new ManageFootballTeam((fetchedCompetition as IFootballHostedCompetition).hostTeam)
            ) ||
              ability.can(RolePolicyActionEnum.UPDATE, new ManageFootballCompetition(fetchedCompetition))) &&
            ((competitionSeasonInvitedTeams.length > 0 &&
              selectedGlobalSeasonOption.secondaryValue?.status === SeasonStatusEnum.Active) ||
              !isGroupShow)
          }
          primaryButtonTitle="Invite Team"
          onButtonClick={() => setTeamInvite(true)}
          onToggleShowHide={handleToggleShowHide}
          withoutFlyMenu
        >
          <AppBox gap="xs" flexDirection="column">
            {competitionSeasonInvitedTeams.length === 0 ? (
              <EmptyTeamInviteComponent
                isPastSeason={
                  selectedGlobalSeasonOption.value
                    ? selectedGlobalSeasonOption.secondaryValue?.status !== SeasonStatusEnum.Active
                    : undefined
                }
                onInviteClick={() => {
                  setTeamInvite(true);
                }}
              />
            ) : (
              <AppPaper padding="none" shadow="xs" style={{ maxHeight: "20.5rem" }} className="overflow-y-auto">
                <AppGridBox columns={2} gap="xs" px="xs" pb="xs">
                  {competitionSeasonInvitedTeams.mapArray((invitation, index) => (
                    <TournamentSeasonInvitedTeamCard key={index} invitedTeam={invitation as any} withBorder />
                  ))}
                </AppGridBox>
              </AppPaper>
            )}
          </AppBox>
        </AppGroup>
      )}
      <AppModal
        opened={isTeamInvite}
        onClose={() => {
          setTeamInvite(false);
        }}
        titleIcon={<IconAddCircle />}
        title="Invite Teams To The Season"
        withoutButtons
      >
        <TeamSearch
          competitionId={fetchedCompetition?.id}
          seasonId={fetchedCompetition.latestSeason?.id}
          parentTeam={(fetchedCompetition as IFootballHostedCompetition)?.hostTeam}
          isTeamInvite
          onSelectedTeam={handleSelectedTeams}
        />
      </AppModal>
    </>
  );
};
